import React, { useMemo } from "react";
import type { DriverDeliveryCardItem } from "../../types/driverDeliveries";
import { formatMoney } from "../../utils/driverDeliveries";

type DriverDeliveryDetailModalProps = {
  item: DriverDeliveryCardItem | null;
  onClose: () => void;
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(8, 10, 18, 0.48)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  zIndex: 999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "800px",
  maxHeight: "84vh",
  border: "1px solid var(--border)",
  borderRadius: "28px",
  background: "var(--surface)",
  boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "center",
  padding: "18px 22px",
  borderBottom: "1px solid var(--border)",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginTop: "4px",
};

const closeButtonStyle: React.CSSProperties = {
  minHeight: "42px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 16px",
  fontWeight: 700,
  cursor: "pointer",
};

const bodyStyle: React.CSSProperties = {
  padding: "20px 22px 22px 22px",
  overflowY: "auto",
  flex: 1,
};

const metricsStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "14px",
  marginBottom: "20px",
};

const metricCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "20px",
  background: "var(--surface-alt)",
  padding: "14px 16px",
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: "11px",
  color: "var(--text-secondary)",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: 700,
};

const metricValueStyle: React.CSSProperties = {
  fontSize: "20px",
  fontWeight: 900,
  color: "var(--text-primary)",
};

const negativeStyle: React.CSSProperties = {
  color: "var(--danger)",
};

const sectionStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "22px",
  overflow: "hidden",
  background: "var(--surface)",
  marginTop: "16px",
};

const sectionHeaderStyle: React.CSSProperties = {
  padding: "14px 16px",
  background: "var(--surface-alt)",
  borderBottom: "1px solid var(--border)",
  fontSize: "14px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "190px minmax(0, 1fr)",
  gap: "14px",
  padding: "12px 16px",
  borderBottom: "1px solid var(--border)",
};

const keyStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const valueStyle: React.CSSProperties = {
  fontSize: "14px",
  color: "var(--text-primary)",
  wordBreak: "break-word",
  fontWeight: 600,
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return 0;

  const raw = value.trim();
  if (!raw) return 0;

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let normalized = raw;

  if (hasComma && hasDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = raw.replace(",", ".");
  } else if (hasDot) {
    normalized = /^\d+\.\d+$/.test(raw) ? raw : raw.replace(/\./g, "");
  }

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeCommissionPercentage = (value: unknown): number => {
  const numeric = toNumber(value);

  if (!numeric) return 0;

  // Legacy: 0.2 = 20%
  if (numeric > 0 && numeric <= 1) return numeric;

  // Fallback: 20 = 20%
  if (numeric > 1) return numeric / 100;

  return 0;
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const DriverDeliveryDetailModal = ({
  item,
  onClose,
}: DriverDeliveryDetailModalProps) => {
  const entries = useMemo(() => {
    if (!item) return [];
    return Object.entries(item.row);
  }, [item]);

  if (!item) return null;

  const bruto =
    Number(item.bruto || 0) ||
    Number(item.row._bruto_envio || 0) ||
    toNumber(item.row.precio_chofer);

  const porcentajeComision =
    Number(item.porcentajeComision || 0) ||
    Number(item.row._porcentaje_comision || 0) ||
    normalizeCommissionPercentage(item.row.porcentaje_chofer);

  const comision =
    Number(item.comision || 0) ||
    Number(item.row._comision_envio || 0) ||
    bruto * porcentajeComision;

  const neto =
    Number(item.neto || 0) ||
    Number(item.row._neto_envio || 0) ||
    bruto - comision;

  const mainData = [
    ["Tracking", item.row.numero_tracking],
    ["Estado", item.row.estado],
    ["Dirección", item.row.direccion],
    ["Cliente", item.row.nombre_fantasia],
    ["Fecha colecta", item.row.fecha_colecta],
    ["Fecha estado", item.row.fecha_estado],
  ];

  const excludedKeys = new Set([
    "numero_tracking",
    "estado",
    "direccion",
    "nombre_fantasia",
    "fecha_colecta",
    "fecha_estado",
    "precio_chofer",
    "porcentaje_chofer",
    "_bruto_envio",
    "_porcentaje_comision",
    "_comision_envio",
    "_neto_envio",
    "_bonus_candidate",
    "_colecta_day",
    "_late_after_23",
    "_penalized_day",
    "BONUS",
  ]);

  const extraData = entries.filter(([key]) => !excludedKeys.has(key));

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={titleStyle}>Detalle del envío</div>
            <div style={subtitleStyle}>
              {item.row.numero_tracking
                ? `Tracking: ${item.row.numero_tracking}`
                : "Sin tracking"}
            </div>
          </div>

          <button style={closeButtonStyle} onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div style={bodyStyle}>
          <div style={metricsStyle}>
            <div style={metricCardStyle}>
              <div style={metricLabelStyle}>Bruto</div>
              <div style={metricValueStyle}>$ {formatMoney(bruto)}</div>
            </div>

            <div style={metricCardStyle}>
              <div style={metricLabelStyle}>Comisión</div>
              <div style={{ ...metricValueStyle, ...negativeStyle }}>
                -$ {formatMoney(comision)}
              </div>
            </div>

            <div style={metricCardStyle}>
              <div style={metricLabelStyle}>Neto</div>
              <div style={metricValueStyle}>$ {formatMoney(neto)}</div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>Datos principales</div>
            {mainData.map(([key, value], index) => (
              <div
                key={String(key)}
                style={{
                  ...rowStyle,
                  borderBottom:
                    index === mainData.length - 1
                      ? "none"
                      : "1px solid var(--border)",
                }}
              >
                <div style={keyStyle}>{key}</div>
                <div style={valueStyle}>{formatValue(value)}</div>
              </div>
            ))}
          </div>

          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>Detalle operativo</div>
            <div style={rowStyle}>
              <div style={keyStyle}>Precio chofer bruto</div>
              <div style={valueStyle}>$ {formatMoney(bruto)}</div>
            </div>
            <div style={rowStyle}>
              <div style={keyStyle}>Porcentaje comisión</div>
              <div style={valueStyle}>
                {(porcentajeComision * 100).toFixed(2)}%
              </div>
            </div>
            <div style={rowStyle}>
              <div style={keyStyle}>Comisión</div>
              <div style={{ ...valueStyle, ...negativeStyle }}>
                -$ {formatMoney(comision)}
              </div>
            </div>
            <div style={{ ...rowStyle, borderBottom: "none" }}>
              <div style={keyStyle}>Neto chofer</div>
              <div style={valueStyle}>$ {formatMoney(neto)}</div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>Datos completos</div>
            {extraData.map(([key, value], index) => (
              <div
                key={key}
                style={{
                  ...rowStyle,
                  borderBottom:
                    index === extraData.length - 1
                      ? "none"
                      : "1px solid var(--border)",
                }}
              >
                <div style={keyStyle}>{key}</div>
                <div style={valueStyle}>{formatValue(value)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDeliveryDetailModal;